/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { formatRFC3339, format } from 'date-fns';
import ApiService from '@services/api';
import { useSession } from '@hooks/useSession';
import {
  MdDownload,
  MdRemoveRedEye,
  MdContentCopy,
  MdPrint,
  MdEmail,
} from 'react-icons/md';

import { downloadFile } from '@utils/download';
import Loading from '@components/Loading';
import RoundButton from '@components/RoundButton';
import Modal from '@components/Modal';
import NotAuthenticated from '@components/NotAuthenticated';
import SelectInput from '@components/SelectInput';
import Input from '@components/Input';
import Button from '@components/Button';
import Paginator from '@components/Paginator';
import settingsStore from '@store/SettingsStore';
import { StatusColor } from '@styles/colors';
import SendEmailDialog from '@components/SendEmailDialog';
import ICTe from '../../interfaces/ICTe';

import {
  Container,
  Title,
  Selection,
  Row,
  Field,
  Buttons,
  DateInput,
  Table,
  ContainerEmissao,
  WrapperStatus,
  ContainerStatus,
  Status,
  Actions,
} from './styles';

const currencyFormat = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CTeDocs: React.FC = observer(() => {
  const params = useParams();
  const { isMigrating } = useSession();
  const { api } = new ApiService(params.token);

  const modeloOptions = useMemo(() => {
    return [
      { label: 'Todos', value: 1 },
      { label: '57 (CT-e)', value: 57 },
      { label: '67 (CT-e OS)', value: 67 },
    ].filter((option) => {
      if (option.value === 1) return true;
      return settingsStore.models.includes(option.value.toString());
    });
  }, [settingsStore.models]);

  const [dataIniSel, setDataIniSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [dataFimSel, setDataFimSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [modeloSel, setModeloSel] = useState(modeloOptions[0]);
  const [serieSel, setSerieSel] = useState('');
  const [nroIniSel, setNroIniSel] = useState('');
  const [nroFimSel, setNroFimSel] = useState('');

  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [rejeicao, setRejeicao] = useState('');
  const [ctes, setCtes] = useState<ICTe[]>([]);
  const [page, setPage] = useState(1);
  const [totalCtes, setTotalCtes] = useState(100);
  const [queryCount, setQueryCount] = useState(0);
  const [emailModalData, setEmailModalData] = useState<{
    cte: ICTe;
    email: string;
  } | null>(null);

  useEffect(() => {
    if (!isMigrating) {
      loadCtes();
    }
  }, [page, isMigrating]);

  async function loadCtes() {
    try {
      setLoading(true);

      const queryParams: any = {
        page,
        dateStart: dataIniSel,
        dateEnd: dataFimSel,
      };
      if (modeloSel.value !== 1) {
        queryParams.model = modeloSel.value;
      }
      if (serieSel) {
        queryParams.serie = serieSel;
      }
      if (nroIniSel) {
        queryParams.numberStart = nroIniSel;
      }
      if (nroFimSel) {
        queryParams.numberEnd = nroFimSel;
      }

      const resp = await api.get('/cte/docs', {
        params: queryParams,
      });
      setCtes(resp.data.items);
      setTotalCtes(resp.data.total);
      setQueryCount(resp.data.queryCount);
      setAuthenticated(true);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setAuthenticated(false);
      } else {
        console.log(error.stack);
        toast.error('Falha ao carregar');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(cte: ICTe) {
    try {
      const resp = await api.get(`/cte/docs/xml/${cte.id}`, {
        responseType: 'blob',
      });
      if (cte.statusId === 101) {
        const dh = format(new Date(), 'yyyyMMdd-HHmmss');
        downloadFile(resp.data, `xml-cte-${dh}.zip`);
      } else {
        downloadFile(resp.data, `${cte.protocol}_v${cte.version}-procCTe.xml`);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('Download indisponível');
      } else {
        toast.error('Falha ao realizar o download');
      }
    }
  }

  async function handlePDF(cte: ICTe) {
    try {
      const resp = await api.get(`/pdf/cte/${cte.key}`, {
        responseType: 'blob',
      });
      const urlObj = window.URL.createObjectURL(resp.data);
      window.open(urlObj, '_blank')?.focus();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('PDF indisponível');
      } else {
        toast.error('Falha ao gerar o PDF');
      }
    }
  }

  async function handleEmail(cte: ICTe, recipientEmail: string) {
    try {
      await api.post(`/email/cte/${cte.key}`, {
        email: recipientEmail,
      });
      toast.success('E-mail agendado com sucesso');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Falha ao enviar E-mail');
    }
  }

  async function handleCopyClipboard(key: string) {
    try {
      const textField = document.createElement('textarea');
      textField.innerText = key;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand('copy');
      textField.remove();
      toast.success('Chave copiada para a área de transferência');
    } catch (error) {
      toast.error('Falha ao copiar a chave de acesso');
    }
  }

  function renderEmissao(nota: ICTe) {
    return (
      <ContainerEmissao>
        {format(new Date(nota.issueDate), 'dd/MM/yyyy HH:mm')}
      </ContainerEmissao>
    );
  }

  function renderStatus(cStat: number, xStat: string) {
    if (cStat === 100)
      return (
        <Status style={{ background: StatusColor.completed }}>
          Autorizado
        </Status>
      );
    if (cStat === 101)
      return (
        <Status style={{ background: StatusColor.cancelled }}>Cancelado</Status>
      );
    if (cStat > 200)
      return (
        <>
          <Status style={{ background: StatusColor.failed }}>Rejeitado</Status>{' '}
          <RoundButton
            hint="Visualizar Rejeição"
            onClick={() => setRejeicao(xStat)}
          >
            <MdRemoveRedEye size={18} />
          </RoundButton>
        </>
      );
    return xStat;
  }

  if (loading)
    return (
      <Container>
        <Loading />
      </Container>
    );

  if (!authenticated) {
    return <NotAuthenticated />;
  }

  return (
    <Container>
      <Title>CONHECIMENTOS DE TRANSPORTE</Title>
      <Selection>
        <Row>
          <div style={{ marginRight: 10 }}>
            <Field>
              Emissão
              <div>
                <DateInput
                  value={dataIniSel}
                  onChange={(e: any) => setDataIniSel(e.target.value)}
                />{' '}
                a{' '}
                <DateInput
                  value={dataFimSel}
                  onChange={(e: any) => setDataFimSel(e.target.value)}
                />
              </div>
            </Field>
          </div>
          <Field>
            Modelo
            <SelectInput
              fullWidth
              options={modeloOptions}
              defaultValue={modeloOptions[0]}
              value={modeloSel}
              onChange={(newValue: any) => setModeloSel(newValue)}
            />
          </Field>
          <Field>
            Série
            <Input
              onlyNumber
              value={serieSel}
              onChange={(e: any) => {
                setSerieSel(e.target.value);
              }}
            />
          </Field>
          <Field>
            Número Inicial
            <Input
              onlyNumber
              value={nroIniSel}
              onChange={(e: any) => setNroIniSel(e.target.value)}
            />
          </Field>
          <Field>
            Número Final
            <Input
              onlyNumber
              value={nroFimSel}
              onChange={(e: any) => setNroFimSel(e.target.value)}
            />
          </Field>
        </Row>

        <Buttons>
          <Button
            onClick={() => {
              setPage(1);
              loadCtes();
            }}
          >
            Consultar
          </Button>
        </Buttons>
      </Selection>

      {ctes.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Número</th>
                <th style={{ width: '8%' }}>Série</th>
                <th style={{ width: '5%' }}>Modelo</th>
                <th style={{ width: '20%' }}>Emissão</th>
                <th style={{ width: '20%' }}>Destinatário / Tomador</th>
                <th style={{ width: '10%' }}>Valor</th>
                <th style={{ width: '20%' }}>Status</th>
                <th style={{ width: '7%' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {ctes.map((cte) => (
                <tr key={cte.id}>
                  <td>{cte.number}</td>
                  <td>{cte.serie}</td>
                  <td>{cte.model}</td>
                  <td>{renderEmissao(cte)}</td>
                  <td>{cte?.receiver?.name}</td>
                  <td>{cte.total ? currencyFormat.format(cte.total) : ''}</td>
                  <td>
                    <WrapperStatus>
                      <ContainerStatus>
                        {cte.receiptDate &&
                          format(new Date(cte.receiptDate), 'dd/MM/yyyy HH:mm')}
                        {renderStatus(cte.statusId, cte.status)}
                      </ContainerStatus>
                    </WrapperStatus>
                  </td>
                  <td>
                    <Actions>
                      <RoundButton
                        hint="Copiar Chave de Acesso"
                        onClick={() => handleCopyClipboard(cte.key)}
                      >
                        <MdContentCopy size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Download"
                        onClick={() => handleDownload(cte)}
                      >
                        <MdDownload size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Gerar PDF"
                        onClick={() => handlePDF(cte)}
                      >
                        <MdPrint size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Enviar E-mail"
                        onClick={() =>
                          setEmailModalData({
                            cte,
                            email: (cte as any).receiver?.email || '',
                          })
                        }
                      >
                        <MdEmail size={18} />
                      </RoundButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div>
            Exibindo {page > 1 ? (page - 1) * 20 + 1 : 1} a{' '}
            {page > 1 ? (page - 1) * 20 + queryCount : queryCount} de um total
            de {totalCtes} transportes.
          </div>
        </>
      )}

      {totalCtes > 20 && (
        <Paginator
          activePage={Number(page)}
          limitPage={20}
          totalRecords={totalCtes}
          onChange={(newPage) => setPage(newPage)}
        />
      )}

      {rejeicao && (
        <Modal onCloseRequest={() => setRejeicao('')} exitButton={false}>
          <div style={{ padding: 40 }}>{rejeicao}</div>
        </Modal>
      )}

      {emailModalData && (
        <SendEmailDialog
          isOpen={!!emailModalData}
          initialEmail={emailModalData.email}
          onConfirm={(email) => {
            handleEmail(emailModalData.cte, email);
            setEmailModalData(null);
          }}
          onCancel={() => setEmailModalData(null)}
        />
      )}
    </Container>
  );
});

export default CTeDocs;
